import type { RichBlock, RichInline, RichListItem, RichText } from '@mai-work-report/contracts';
import Box from '@mui/material/Box';
import type { ReactNode } from 'react';

/** Text styles shared by the editor and the read-only view (wrapping, lists, paragraphs). */
export const RICH_TEXT_STYLES = {
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  wordBreak: 'normal',
  '& p': { margin: 0, minHeight: '1.5em' },
  '& ul, & ol': { margin: 0, paddingLeft: '1.5em' },
} as const;

function renderInline(node: RichInline, key: number): ReactNode {
  if (node.type === 'hardBreak') {
    return <br key={key} />;
  }
  let content: ReactNode = node.text;
  for (const mark of node.marks ?? []) {
    switch (mark.type) {
      case 'bold':
        content = <strong>{content}</strong>;
        break;
      case 'italic':
        content = <em>{content}</em>;
        break;
      case 'underline':
        content = <u>{content}</u>;
        break;
      case 'strike':
        content = <s>{content}</s>;
        break;
      case 'link':
        content = (
          <a href={mark.attrs.href} target="_blank" rel="noopener noreferrer nofollow">
            {content}
          </a>
        );
        break;
    }
  }
  return <span key={key}>{content}</span>;
}

function renderItems(items: readonly RichListItem[]): ReactNode[] {
  return items.map((item, index) => <li key={index}>{item.content.map(renderBlock)}</li>);
}

function renderBlock(block: RichBlock, key: number): ReactNode {
  switch (block.type) {
    case 'paragraph':
      return <p key={key}>{(block.content ?? []).map(renderInline)}</p>;
    case 'bulletList':
      return <ul key={key}>{renderItems(block.content)}</ul>;
    case 'orderedList':
      return (
        <ol key={key} start={block.attrs?.start ?? 1}>
          {renderItems(block.content)}
        </ol>
      );
  }
}

/** Read-only rendering of a stored document (React elements only; no HTML is injected). */
export function RichTextView({ value }: { value: RichText }) {
  return <Box sx={RICH_TEXT_STYLES}>{(value.content ?? []).map(renderBlock)}</Box>;
}
